import { ArrowRight, BookOpen } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { docsApi, type ResolvedPlacement } from "@/client/features/docs/docs-api";
import { PlacementsContext, usePlacements } from "@/client/features/docs/placements-context";

/**
 * Loads the caller's resolved doc placements once and shares them with every
 * customer page. Placements are a non-critical enhancement (contextual links to
 * guides), so a load failure degrades silently to "no guides" rather than
 * surfacing an error - the underlying features still work without them.
 */

export function PlacementsProvider({ children }: { children: ReactNode }) {
  const [placements, setPlacements] = useState<ResolvedPlacement[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await docsApi.listPlacements();
        if (!cancelled) setPlacements(res.placements);
      } catch {
        // Silent: guides are optional contextual links.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <PlacementsContext.Provider value={placements}>{children}</PlacementsContext.Provider>;
}

/**
 * A compact inline "help" link for a single-guide slot. Renders nothing when no
 * guide is mapped to the slot for this tenant, so it's safe to drop into any
 * page header.
 */
export function GuideLink({ slot, className }: { slot: string; className?: string }) {
  const [first] = usePlacements(slot);
  if (!first) return null;
  return (
    <Link
      to={`/docs/${encodeURIComponent(first.slug)}`}
      className={`text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm underline-offset-4 hover:underline ${className ?? ""}`}
    >
      <BookOpen className="size-3.5" />
      {first.title}
    </Link>
  );
}

/**
 * Guide cards for a multi-item slot (e.g. the dashboard). Renders nothing when
 * the slot is empty for this tenant.
 */
export function GuideCards({ slot, heading }: { slot: string; heading?: string }) {
  const placements = usePlacements(slot);
  if (placements.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BookOpen className="text-muted-foreground size-4" />
          {heading ?? "Guides"}
        </CardTitle>
        <CardDescription>Documentation to help you get started.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2">
          {placements.map((p) => (
            <Link
              key={`${p.slot}:${p.slug}`}
              to={`/docs/${encodeURIComponent(p.slug)}`}
              className="hover:border-foreground/30 hover:bg-muted/40 group flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{p.title}</p>
                {p.description ? (
                  <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
                    {p.description}
                  </p>
                ) : null}
              </div>
              <ArrowRight className="text-muted-foreground group-hover:text-foreground mt-0.5 size-4 shrink-0 transition-colors" />
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
